import { observer } from 'mobx-react'
import { type FC, useState } from 'react'
import Button from '../../App/Button'
import { useStore } from '../../stores'
import style from './Header.module.css'
import { Link, NavLink, useLocation } from 'react-router-dom'
import { ReactComponent as HomeIcon } from '../../assets/images/icons/home.svg'
import { ReactComponent as MapIcon } from '../../assets/images/icons/map.svg'
import { ReactComponent as ChartIcon } from '../../assets/images/icons/chart.svg'
import { ReactComponent as ChevronDownIcon } from '../../assets/images/icons/arrow-ios-down.svg'
import { ReactComponent as ChevronRightIcon } from '../../assets/images/icons/arrow-ios-right.svg'
import EnekaLogo from '../../assets/images/eneka.svg'
import { type Project } from '../../stores/features'
import hotjar from '../../hotjar'
import { useOutSideClickListener } from 'helpers'
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch'

const Header: FC = observer(() => {
  const {
    user,
    app: appStore,
    monitor: monitorStore,
    view: viewStore,
    filter: filterStore
  } = useStore()
  const { pathname } = useLocation()
  const [profileMenuOpen, setProfileMenuOpen] = useState(false)
  const [projectListOpen, setProjectListOpen] = useState(false)

  const profileMenuRef = useOutSideClickListener<HTMLDivElement>(() => {
    setProfileMenuOpen(false)
  })
  const projectListMenuRef = useOutSideClickListener<HTMLDivElement>(() => {
    setProjectListOpen(false)
  })

  const clearHistoryVariables = (): void => {
    appStore.setFromHistorySelection(false)
    appStore.setCurrentHistoryProject(null)
    monitorStore.setModalReportOpen(false)
    monitorStore.setModalFormOpen(false)
    monitorStore.setModalPrintOpen(false)
    monitorStore.setEditMode(false)
  }

  const onLogoutClick = (): void => {
    hotjar.event('user_logout')
    user.logout()
  }

  const toggleProfileMenu = (): void => {
    hotjar.event('user_toggle_profile_menu')
    setProfileMenuOpen((state) => {
      if (!state) {
        setProjectListOpen(false)
      }
      return !state
    })
  }

  const toggleProjectList = (): void => {
    hotjar.event('user_toggle_project_menu')
    setProjectListOpen((state) => {
      if (!state) {
        setProfileMenuOpen(false)
      }
      return !state
    })
  }

  const handleProjectClick = (p: Project) => () => {
    if (monitorStore.editMode) {
      viewStore.setShowModalRequest(true)
      viewStore.isEditWarning = true
      return
    }
    hotjar.event('user_change_project')
    appStore.selectProject(p)
  }

  return (
    <div className={style.content}>
      <div className={style.logo}>
        <NavLink
          className={style['logo-link']}
          to="/home"
          onClick={clearHistoryVariables}
          id="header-home-navlink-logo-id"
        >
          <img src={EnekaLogo} alt="" className="" />
        </NavLink>
      </div>
      <div className={style.tabbar}>
        <NavLink
          className={style.tab}
          activeClassName={style.active}
          to={`/home/?${filterStore.filterParameter}`}
          onClick={clearHistoryVariables}
          exact
          isActive={() => ['/', '/home'].includes(pathname)}
          id="header-home-navlink-id"
        >
          <HomeIcon />
          <span>Startseite</span>
        </NavLink>
        <NavLink
          className={style.tab}
          to={`/dashboard/?${filterStore.filterParameter}`}
          onClick={clearHistoryVariables}
          activeClassName={style.active}
          id="header-dashboard-navlink-id"
        >
          <ChartIcon />
          <span>Übersicht</span>
        </NavLink>
        <NavLink
          className={style.tab}
          to={`/map/?${filterStore.filterParameter}`}
          onClick={clearHistoryVariables}
          data-testid="header-map-link"
          activeClassName={[style.active, style.mapTab].join(' ')}
          id="header-map-navlink-id"
        >
          <MapIcon />
          <span>Karte</span>
        </NavLink>
      </div>
      <div className="flex justify-end flex-auto border-b border-gray-athens w-48">
        <div className="relative flex items-center h-full">
          {process.env.REACT_APP_DEMO_STATUS === 'true' && (
            <div className={style.demoLabel}>
              Demo Version
              <span className="animate-ping ml-2 inline-flex h-3 w-3 rounded-full bg-red-punch opacity-75"></span>
            </div>
          )}
          {appStore.selectedProject && (
            <div
              className={[
                style['project-menu-container'],
                projectListOpen ? style.active : ''
              ].join(' ')}
              onClick={toggleProjectList}
              ref={projectListMenuRef}
            >
              <div className="flex flex-row h-full w-full justify-between items-center">
                <div className="pl-6 flex">
                  <h2 className="font-black">
                    {appStore.selectedProject.title}
                  </h2>
                </div>
                <div className={style['project-menu-icon-container']}>
                  <ChevronDownIcon
                    className={[
                      style['project-menu-icon'],
                      style['project-menu-indicator']
                    ].join(' ')}
                  />
                </div>
              </div>
              <div className={style.menu} role="menu">
                {appStore.userCanEditProjectSettings &&
                  !(appStore.selectedProject.is_scenario ?? false) && (
                    <Link
                      to="/settings"
                      className={style['menu-link-settings']}
                    >
                      <span>Projekt Bearbeiten</span>
                    </Link>
                  )}
                {Array.from(appStore.projects.values()).map((p) => (
                  <div
                    className={style['menu-item']}
                    key={p.title}
                    onClick={handleProjectClick(p)}
                    data-testid={'project-selection-item-' + p.title}
                  >
                    <div className="text-ellipsis truncate">
                      {p.is_scenario && <RocketLaunchIcon className="mr-1" />}
                      {p.title}
                    </div>
                    <div className={'relative flex items-left'}>
                      <ChevronRightIcon
                        className={[style['project-menu-icon'], 'ml-4'].join(
                          ' '
                        )}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
          <div
            id="profileMenu"
            className={[
              style['profile-menu-container'],
              profileMenuOpen ? style.active : ''
            ].join(' ')}
            onClick={toggleProfileMenu}
            ref={profileMenuRef}
          >
            <div>
              <button className={style['profile-button']}>
                <div className={style.avatar}>{user.currentUser?.initials}</div>
              </button>
            </div>
            <div
              className={style.menu}
              role="menu"
              onClick={(e) => {
                e.stopPropagation()
              }}
            >
              <div className={style['menu-item']}>
                <span className="text-base font-black">
                  {user.currentUser?.title ?? ''}
                </span>
              </div>
              <div className={style['menu-item']}>
                <a
                  href="https://manual.eneka.de/"
                  className="text-base "
                  target="_blank"
                  rel="noreferrer"
                >
                  Manual
                </a>
              </div>
              <div className={style['menu-item']}>
                <a
                  href="https://manual.eneka.de/docs/eep/release_notes/"
                  className="text-base "
                  target="_blank"
                  rel="noreferrer"
                >
                  Release-Notes
                </a>
              </div>
              <div className={style['menu-item']}>
                <a
                  href="https://eneka.de/fromapplikation/"
                  className="text-base "
                >
                  Support kontaktieren
                </a>
              </div>
              <div className={style['menu-item']}>
                <Button
                  label="Abmelden"
                  filled
                  customCSS="w-full flex items-center justify-center"
                  size="medium"
                  onClick={onLogoutClick}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
})

export default Header
